import { ampli } from '@/ampli';

import { buttons } from '@/styles/buttons';

import { LoadingSpinner } from '@/ui/Icons/Animated/LoadingSpinner';
import { LockClosedIcon } from '@/ui/Icons/LockClosedIcon';
import { LockOpenIcon } from '@/ui/Icons/LockOpenIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

const LockButton = ({
  isLocked,
  isDisabled,
  onClick,
}: {
  isLocked: boolean;
  isDisabled: boolean;
  onClick?: () => void;
}) => (
  <MatchaTooltip>
    <MatchaTooltipTrigger>
      <button
        className={buttons({
          size: 'small',
          color: 'gray',
          shape: 'pill',
          iconOnly: true,
          class: ['w-9 p-0', isLocked && 'bg-token-gray-300 outline-1 outline-token-gray-500'],
        })}
        disabled={isDisabled}
        onClick={onClick}
      >
        {isLocked ? <LockClosedIcon /> : <LockOpenIcon />}
      </button>
    </MatchaTooltipTrigger>
    <MatchaTooltipContent>{isLocked ? 'Unlock' : 'Lock'} the sell amount</MatchaTooltipContent>
    <MatchaTooltipPopover />
  </MatchaTooltip>
);

const ActionButton = ({
  text,
  isDisabled,
  isLoading = false,
  onClick,
}: {
  text: string;
  isDisabled: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}) => (
  <button
    className={buttons({
      size: 'small',
      color: 'gray',
      shape: 'pill',
      // Position the spinner absolutely over the (invisible) label so the
      // button keeps its natural width while loading and the row doesn't reflow.
      class: ['relative'],
    })}
    disabled={isDisabled || isLoading}
    aria-busy={isLoading || undefined}
    onClick={onClick}
  >
    <span className={isLoading ? 'invisible' : undefined}>{text}</span>
    {isLoading && (
      <span className="absolute inset-0 flex items-center justify-center">
        <LoadingSpinner className="ml-0" />
      </span>
    )}
  </button>
);

export enum BalanceFraction {
  ZERO = 0,
  FULL = 1,
  HALF = 2,
}

type Props = {
  orderType: 'market' | 'limit' | 'cross_chain';
  shouldShowLock?: boolean;
  isLocked?: boolean;
  shouldShowClear: boolean;
  shouldShowActions: boolean;
  isTokenBalanceZero: boolean;
  isDisabled: boolean;
  /**
   * Which sell-fraction button is currently in flight, if any. Drives the
   * inline spinner on the matching button and disables the other action
   * buttons while the click is being processed.
   */
  loadingFraction?: BalanceFraction;
  handleBalancePercentageClick: (balDenominator: BalanceFraction, shortcutLabel: string) => void;
  handleSellAmountLockClick?: () => void;
};

export const SellTokenQuickActions = ({
  orderType,
  shouldShowLock = false,
  isLocked = false,
  shouldShowClear,
  shouldShowActions,
  isTokenBalanceZero,
  isDisabled,
  loadingFraction,
  handleBalancePercentageClick,
  handleSellAmountLockClick,
}: Props) => {
  const isAnyFractionLoading = loadingFraction !== undefined;

  const handleUserAction = (denominator: BalanceFraction, label: '50%' | 'clear' | 'max') => {
    ampli.sellAmountPresetSelected({
      swap_order_type: orderType,
      sell_amount_preset_type: label,
    });

    handleBalancePercentageClick(denominator, label);
  };

  return (
    <div className="flex gap-1.5">
      {shouldShowLock && (
        <LockButton
          isLocked={isLocked}
          isDisabled={isDisabled}
          onClick={handleSellAmountLockClick}
        />
      )}

      {shouldShowClear && (
        <ActionButton
          text="Clear"
          isDisabled={false}
          onClick={() => handleUserAction(0, 'clear')}
        />
      )}

      {shouldShowActions && (
        <>
          <ActionButton
            text="50%"
            isDisabled={isDisabled || isTokenBalanceZero || isAnyFractionLoading}
            isLoading={loadingFraction === BalanceFraction.HALF}
            onClick={() => handleUserAction(BalanceFraction.HALF, '50%')}
          />
          <ActionButton
            text="Max"
            isDisabled={isDisabled || isTokenBalanceZero || isAnyFractionLoading}
            isLoading={loadingFraction === BalanceFraction.FULL}
            onClick={() => handleUserAction(BalanceFraction.FULL, 'max')}
          />
        </>
      )}
    </div>
  );
};
